import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useUser, useUserMutations } from '@klear/ui/hooks/use-user'
import { userToFormSchema, userSchemaToUpdateWire } from '@klear/ui/lib/user-wire'
import { validateUser, type UserSchema, type UserFormErrors } from '@klear/ui/lib/user-form-schema'
import LoadingSpinner from '@klear/ui/components/spinner'
import {
  PERMISSION_ENTITY_USER,
  PERMISSION_ACTION_EDIT,
  TRADER_ROLES_THAT_CAN_MANAGE_ORGAN_USERS,
  ROUTE_TEAM,
  buildNavPathTrader
} from '@klear/constants'
import isEmpty from 'lodash/isEmpty'
import TeamUserForm from '@/app/organization/team-user-form'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function TeamUserEdit() {
  const { user } = useAuth()
  const { traderId, userId } = useParams()
  const navigate = useNavigate()
  const teamPath = `${buildNavPathTrader(traderId!)}/${ROUTE_TEAM}`
  const { updateUser } = useUserMutations()

  const { user: targetUser, isLoading } = useUser(parseInt(userId!))
  useDocumentTitle(targetUser?.fullName, 'Edit Team Member')

  const [values, setValues] = useState<UserSchema | null>(null)
  const [errors, setErrors] = useState<UserFormErrors>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (targetUser) setValues(userToFormSchema(targetUser))
  }, [targetUser])

  const canManage =
    user.hasKlearRole || user.roles?.some((r) => TRADER_ROLES_THAT_CAN_MANAGE_ORGAN_USERS.includes(r.role)) || false

  if (!canManage || !user.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_EDIT)) {
    return null
  }

  if (isLoading || !values) {
    return <LoadingSpinner />
  }

  if (!targetUser) {
    return <div className="p-6 text-muted-foreground">User not found.</div>
  }

  const numericTraderId = parseInt(traderId!)
  if (targetUser.hasOrganizationOwnerRoleForTrader(numericTraderId) && !user.hasKlearRole) {
    return <Navigate to={teamPath} replace />
  }

  const onChange = (patch: Partial<UserSchema>) => setValues((prev) => (prev ? { ...prev, ...patch } : prev))

  const handleSubmit = async () => {
    if (targetUser.id == null || !values) return

    const nextErrors: UserFormErrors = validateUser(values)
    if (!values.roles?.[0]?.role) nextErrors.roles = 'User must have one role'

    setErrors(nextErrors)
    if (!isEmpty(nextErrors)) return

    setSubmitting(true)
    try {
      await updateUser({ id: targetUser.id, data: userSchemaToUpdateWire(values) })
      navigate(teamPath)
    } catch {
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <TeamUserForm
      values={values}
      errors={errors}
      onChange={onChange}
      isNewUser={false}
      onSubmit={handleSubmit}
      cancelPath={teamPath}
      title="Edit Team Member"
      submitting={submitting}
    />
  )
}
